import { ALL_CALCULATIONS } from "@suse-o11y/calculate-value-from-points";
import type { Optional } from "@suse-o11y/optional";
import { z } from "zod";

import type { GaugeChart as OpenApiGaugeChart } from "~/main/common/openapi";

import { DEFAULT as DEFAULT_SETTINGS, type Settings } from "../../../components/widget-form/gauge-chart/settings";
import { dashboardWidgetFormatSchema } from "../_dashboard-widget-format";

import { ThresholdsMode, ThresholdsSteps } from "./_sub-schemas";

const schema = z
	.object({
		thresholds: z
			.object({
				defaultColor: z
					.string()
					.trim()
					.optional()
					.catch(undefined)
					.transform((color) => color || DEFAULT_SETTINGS.color),

				mode: ThresholdsMode.schema.optional().catch(undefined),
				steps: ThresholdsSteps.schema,
			})
			.optional()
			.catch(undefined),

		max: z.number().optional().catch(undefined),

		format: dashboardWidgetFormatSchema.optional().catch(undefined),

		calculation: z.enum(ALL_CALCULATIONS).optional().catch(undefined),
	})
	.catch({});

type GaugeChart = z.infer<typeof schema>;

function fromSettings(settings: undefined | Settings): GaugeChart {
	return {
		max: settings?.max,
		calculation: settings?.calculation,

		format: {
			unit: settings?.unit,
			decimalPlaces: settings?.decimals,
		},

		thresholds: {
			defaultColor: settings?.color ?? DEFAULT_SETTINGS.color,
			mode: settings?.thresholds?.mode,
			steps: ThresholdsSteps.fromSettings(settings?.thresholds?.steps),
		},
	};
}

function toSettings(spec: undefined | GaugeChart): Settings {
	return {
		unit: spec?.format?.unit,
		decimals: spec?.format?.decimalPlaces,
		max: spec?.max,
		calculation: spec?.calculation,
		color: spec?.thresholds?.defaultColor,
		thresholds: {
			mode: spec?.thresholds?.mode,
			steps: spec?.thresholds?.steps,
		},
	} satisfies Optional<Settings>;
}

function openApiToSettings(chart: OpenApiGaugeChart): Settings {
	const { calculation, unit, decimals, max, thresholds: ct } = chart;
	return {
		unit,
		decimals,
		max,
		calculation,
		color: ct?.defaultColor,

		thresholds: ct
			? {
					mode: ct.mode as "absolute" | "percent" | undefined,
					steps: ct.steps.map((step) => ({
						value: step.value,
						color: step.color,
					})),
				}
			: undefined,
	};
}

const GaugeChart = { schema, fromSettings, toSettings, openApiToSettings };

export { GaugeChart };
